import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { theme } from '../theme';
import { useMouseParallax } from './ScrollReveal';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const typewriter = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const blink = keyframes`
  0%, 100% { border-color: transparent; }
  50% { border-color: ${theme.colors.primary[500]}; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
  }
  50% {
    box-shadow: 0 0 40px rgba(59, 130, 246, 0.6), 0 0 80px rgba(59, 130, 246, 0.2);
  }
`;

const particleFloat = keyframes`
  0%, 100% {
    transform: translateY(0) rotate(0deg);
    opacity: 0.3;
  }
  25% {
    transform: translateY(-30px) rotate(90deg);
    opacity: 0.8;
  }
  50% {
    transform: translateY(-15px) rotate(180deg);
    opacity: 0.5;
  }
  75% {
    transform: translateY(-40px) rotate(270deg);
    opacity: 0.7;
  }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: ${theme.colors.neutral[50]};
  background-image: 
    radial-gradient(at 0% 0%, ${theme.colors.primary[100]} 0px, transparent 50%),
    radial-gradient(at 100% 100%, ${theme.colors.secondary[100]} 0px, transparent 50%),
    radial-gradient(at 100% 0%, ${theme.colors.accent[50]} 0px, transparent 50%);
  padding: 6rem 2rem 2rem;
  scroll-margin-top: 80px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.4;
    z-index: 0;
  }
  
  @media (max-width: 768px) {
    min-height: auto;
    padding: 5rem 1.75rem 3rem;
    align-items: flex-start;
  }

  @media (max-width: 480px) {
    padding: 4rem 1.25rem 3rem;
  }
`;

const Particle = styled.div`
  position: absolute;
  width: ${props => props.$size || '8px'};
  height: ${props => props.$size || '8px'};
  background: ${props => props.$color || `rgba(59, 130, 246, 0.2)`};
  border-radius: 50%;
  animation: ${particleFloat} ${props => props.$duration || '8s'} ease-in-out infinite;
  animation-delay: ${props => props.$delay || '0s'};
  top: ${props => props.$top};
  left: ${props => props.$left};
  z-index: 0;
  pointer-events: none;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const TypewriterText = styled.span`
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  border-right: 3px solid ${theme.colors.primary[500]};
  animation: 
    ${typewriter} 2s steps(30) 0.5s forwards,
    ${blink} 0.75s step-end infinite;
  width: 0;
  
  @media (max-width: 768px) {
    white-space: normal;
    border-right: none;
    animation: ${slideUp} 0.8s ease-out 0.3s forwards;
    width: auto;
    opacity: 0;
  }
`;

const RoleTag = styled.span`
  display: inline-block;
  background: ${theme.gradients.professional};
  color: white;
  padding: 0.35rem 1rem;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.bold};
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 0.75rem;
  animation: ${slideUp} 0.6s ease-out 0.1s both;
  box-shadow: ${theme.shadows.md};
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const AnimatedMesh = styled.div`
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(ellipse at 20% 80%, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 20%, rgba(16, 185, 129, 0.08) 0%, transparent 50%),
    radial-gradient(ellipse at 50% 50%, rgba(217, 70, 239, 0.05) 0%, transparent 50%);
  background-size: 200% 200%;
  animation: ${gradientShift} 15s ease infinite;
  z-index: 0;
  pointer-events: none;
`;

const OrbitRing = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${props => props.$size || '300px'};
  height: ${props => props.$size || '300px'};
  border: 1px dashed ${props => props.$color || 'rgba(59, 130, 246, 0.12)'};
  border-radius: 50%;
  transform: translate(-50%, -50%) rotate(0deg);
  animation: spin ${props => props.$speed || '30s'} linear infinite;
  z-index: 0;
  pointer-events: none;
  
  @keyframes spin {
    to { transform: translate(-50%, -50%) rotate(360deg); }
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -4px;
    left: 50%;
    width: 8px;
    height: 8px;
    background: ${props => props.$dotColor || 'rgba(59, 130, 246, 0.3)'};
    border-radius: 50%;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 2rem;
  }

  @media (max-width: 480px) {
    gap: 1.75rem;
  }
`;

const TextContent = styled.div`
  animation: ${fadeInLeft} 0.8s ease-out;
  
  @media (max-width: 768px) {
    order: 2;
  }
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.heading};
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: 1rem;
  color: ${theme.colors.neutral[900]};
  line-height: ${theme.typography.lineHeight.tight};
  
  background: linear-gradient(135deg, ${theme.colors.primary[600]}, ${theme.colors.secondary[500]}, ${theme.colors.accent[500]});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  background-size: 200% auto;
  animation: ${shimmer} 3s linear infinite;
`;

const Subtitle = styled.p`
  font-size: clamp(1.1rem, 2.5vw, 1.5rem);
  color: ${theme.colors.neutral[700]};
  margin-bottom: 1.5rem;
  font-weight: ${theme.typography.fontWeight.medium};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const Description = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.neutral[600]};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin-bottom: 2rem;
  max-width: 600px;
  
  @media (max-width: 768px) {
    margin-left: auto;
    margin-right: auto;
  }

  @media (max-width: 480px) {
    font-size: ${theme.typography.fontSize.base};
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  padding: 1rem 2rem;
  background: ${theme.gradients.professional};
  color: white;
  text-decoration: none;
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.base};
  transition: all ${theme.transitions.base};
  margin-right: 1rem;
  box-shadow: ${theme.shadows.md};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.xl};
    
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 1rem;
    width: 100%;
    max-width: 300px;
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1.75rem;
  }
`;

const CVButton = styled.a`
  display: inline-block;
  padding: 1rem 2rem;
  background-color: transparent;
  color: ${theme.colors.primary[600]};
  border: 2px solid ${theme.colors.primary[600]};
  text-decoration: none;
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.base};
  transition: all ${theme.transitions.base};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 100%;
    background: ${theme.colors.primary[600]};
    transition: width 0.3s ease;
    z-index: -1;
  }

  &:hover {
    color: white;
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
    
    &::before {
      width: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 300px;
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1.75rem;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  animation: ${fadeInRight} 0.8s ease-out;
  
  @media (max-width: 768px) {
    order: 1;
    margin: 0 auto;
    max-width: 280px;
  }

  @media (max-width: 480px) {
    max-width: 240px;
  }
`;

const ImageGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  height: 120%;
  background: ${theme.gradients.professional};
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.3;
  animation: ${pulse} 4s ease-in-out infinite;
  z-index: -1;
`;

const ProfileImage = styled.img`
  width: 100%;
  max-width: 400px;
  height: auto;
  border-radius: ${theme.borderRadius['2xl']};
  box-shadow: ${theme.shadows['2xl']};
  transition: transform ${theme.transitions.slow};
  animation: ${float} 6s ease-in-out infinite, ${glowPulse} 4s ease-in-out infinite;
  border: 4px solid white;

  &:hover {
    transform: scale(1.05) rotate(2deg);
  }
`;

const HeroActions = styled.div`
  margin-top: 1.5rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const TechBadges = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const Badge = styled.span`
  padding: 0.5rem 1.25rem;
  background: ${props => props.index === 0 
    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.2))'
    : props.index === 1
    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.2))'
    : props.index === 2
    ? 'linear-gradient(135deg, rgba(217, 70, 239, 0.1), rgba(217, 70, 239, 0.2))'
    : 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(245, 158, 11, 0.2))'};
  color: ${props => props.index === 0 
    ? theme.colors.primary[600]
    : props.index === 1
    ? theme.colors.secondary[600]
    : props.index === 2
    ? theme.colors.accent[600]
    : theme.colors.warning};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  border: 1px solid ${props => props.index === 0 
    ? theme.colors.primary[200]
    : props.index === 1
    ? theme.colors.secondary[200]
    : props.index === 2
    ? theme.colors.accent[200]
    : `${theme.colors.warning}33`};
  transition: all ${theme.transitions.base};
  animation: ${fadeInUp} 0.6s ease-out ${props => 0.3 + props.index * 0.1}s both;

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${theme.shadows.md};
  }
`;

function Hero() {
  const technologies = ['Robotics', 'IoT', 'AI', 'Embedded Systems', 'Flutter'];
  const [parallaxRef, parallaxOffset] = useMouseParallax(15);

  return (
    <HeroSection id="hero" ref={parallaxRef}>
      {/* Animated gradient mesh */}
      <AnimatedMesh />
      
      {/* Floating particles */}
      <Particle $top="10%" $left="8%" $size="14px" $color="rgba(59, 130, 246, 0.15)" $duration="10s" $delay="0s" />
      <Particle $top="20%" $left="85%" $size="10px" $color="rgba(16, 185, 129, 0.2)" $duration="8s" $delay="1s" />
      <Particle $top="55%" $left="3%" $size="12px" $color="rgba(217, 70, 239, 0.15)" $duration="12s" $delay="2s" />
      <Particle $top="65%" $left="92%" $size="8px" $color="rgba(59, 130, 246, 0.2)" $duration="9s" $delay="0.5s" />
      <Particle $top="35%" $left="78%" $size="16px" $color="rgba(245, 158, 11, 0.12)" $duration="11s" $delay="1.5s" />
      <Particle $top="75%" $left="18%" $size="10px" $color="rgba(16, 185, 129, 0.15)" $duration="7s" $delay="3s" />
      <Particle $top="45%" $left="50%" $size="6px" $color="rgba(59, 130, 246, 0.1)" $duration="14s" $delay="4s" />
      <Particle $top="85%" $left="65%" $size="12px" $color="rgba(217, 70, 239, 0.1)" $duration="13s" $delay="2.5s" />

      <HeroContent>
        <TextContent>
          <RoleTag>CEO & Founder of KaTEK</RoleTag>
          <Title>Hi, I'm Kabil Mbarki</Title>
          <Subtitle>
            <TypewriterText>Robotics, IoT & AI Systems Engineer</TypewriterText>
          </Subtitle>
          <TechBadges>
            {technologies.map((tech, index) => (
              <Badge key={index} index={index}>{tech}</Badge>
            ))}
          </TechBadges>
          <Description>
            Computer Engineer and CEO of KaTEK, specialized in Robotics, Embedded Systems, 
            and AI-driven solutions. I design and lead the development of intelligent systems 
            that bridge hardware and software to create real-world impact.
          </Description>

          <HeroActions>
            <CTAButton href="#contact">Get in Touch</CTAButton>
            <CVButton href="/CV.pdf" target="_blank" rel="noopener noreferrer">Download CV</CVButton>
          </HeroActions>
        </TextContent>
        <ImageContainer>
          <OrbitRing $size="350px" $speed="25s" $color="rgba(59, 130, 246, 0.1)" $dotColor="rgba(59, 130, 246, 0.4)" />
          <OrbitRing $size="450px" $speed="35s" $color="rgba(16, 185, 129, 0.08)" $dotColor="rgba(16, 185, 129, 0.3)" />
          <ImageGlow />
          <ProfileImage
            src="/kabil_1_no_bg.png"
            alt="Kabil Mbarki - CEO & Founder of KaTEK"
            style={{
              transform: `translate(${parallaxOffset.x * 0.5}px, ${parallaxOffset.y * 0.5}px)`,
            }}
          />
        </ImageContainer>
      </HeroContent>
    </HeroSection>
  );
}

export default Hero;
